import { Box, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useEffect, useRef, useState } from 'react';

import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useOptIns } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantDataOptIn/hooks/useOptIns';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface MerchantDataOptInProps {
  opened: boolean;
  setOpened: (opened: boolean) => void;
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
}

export const MerchantDataOptIn = ({ opened, setOpened, paymentMethodDetailEntity }: MerchantDataOptInProps) => {
  const { sendOptInEmail } = useOptIns(paymentMethodDetailEntity);
  const { palette, customBorderRadius } = useCustomTheme();

  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (opened) {
      onResendEmail();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [opened]);

  const onResendEmail = async () => {
    setLoading(true);
    setTimer(30);
    await sendOptInEmail();
    setLoading(false);

    const newInterval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    intervalRef.current = newInterval;
  };

  const email = paymentMethodDetailEntity.onboardingDetails(OnboardingDetailsRole.BORROWER)?.invitation.email;

  if (!email) {
    return null;
  }

  return (
    <NemuruDialog
      opened={opened}
      setOpened={setOpened}
      dialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      buttons={[
        <StandardButton
          main
          key="resendEmail"
          loading={loading}
          onClick={onResendEmail}
          disabled={timer > 0}
          style={{ minWidth: '218px' }}
        >
          <Text tId={timer > 0 ? 'RESEND_EMAIL_IN' : 'RESEND_EMAIL'} dynamicDataTexts={[timer.toString()]} />
        </StandardButton>,
      ]}
    >
      <div>
        <Box mb={3}>
          <Font variant="h3" color="title">
            <Text strong tId="OPT_IN_EMAIL" />
          </Font>
        </Box>
        <CustomPaper fill="paper" style={{ padding: 16 }}>
          <Box display="flex" alignItems="flex-start" gridGap={16}>
            <Icon size={20}>email</Icon>
            <Box>
              <Font variant="body2" color="title" display="block">
                <strong>
                  <Text tId="OPT_IN_EMAIL_SENT_TO" /> {email}
                </strong>
              </Font>
              <Font variant="body2" color="textPrimary" display="block">
                <Text tId="OPT_IN_EMAIL_SUBTITLE" />
              </Font>
            </Box>
          </Box>
        </CustomPaper>
        <Box width="100%" mt={3}>
          <Alert
            severity="info"
            style={{ borderRadius: customBorderRadius.paper, padding: 16 }}
            icon={
              <Box display="flex" alignItems="center" justifyContent="center" width={22} height={22} flexShrink={0}>
                <CircularProgress size={18} style={{ color: palette.info.main }} />
              </Box>
            }
          >
            <Box display="flex" flexDirection="column" gridGap={16}>
              <Box>
                <Font variant="body2" display="block">
                  <Text strong tId="OPT_IN_WAITING_FOR_ACCEPTANCE_TITLE" />
                </Font>
                <Font variant="body2" display="block">
                  <Text tId="OPT_IN_WAITING_FOR_ACCEPTANCE_DESCRIPTION" />
                </Font>
              </Box>
            </Box>
          </Alert>
        </Box>
      </div>
    </NemuruDialog>
  );
};
